/**
 * RapportNotesPreview — Impression des notes de devoirs par groupe
 * Filtrable par Pôle, Niveau, Parcours, Option, Classe
 */

import React, { useState } from 'react';
import { Printer, FileText, Filter, X, Loader2 } from 'lucide-react';
import { useLazyGetRapportImpressionQuery, RapportImpressionResponse, RapportNoteMatiere } from '../../services/notesDevoirsApi';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { getAnneeAcademique } from '../../services/baseApi';

interface RapportNotesPreviewProps {
  onClose: () => void;
}

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];
const POLES = ['Polytechnique', 'Commerce', 'Juridique'];

function buildRapportHTML(data: RapportImpressionResponse, anneeAcademique: string): string {
  const filtresText = Object.entries(data.filtres)
    .filter(([k]) => k !== 'semestre')
    .map(([k, v]) => {
      const labels: Record<string, string> = {
        pole: 'Pôle', niveau: 'Niveau', parcours: 'Parcours',
        option: 'Option', classe: 'Classe'
      };
      return `${labels[k] || k}: ${v}`;
    })
    .join(' — ');

  const semestre = data.filtres.semestre || 'S1';

  const matieresSections = data.matieres.map((mat: RapportNoteMatiere, idx: number) => {
    const rows = mat.notes.map((n, i) => {
      const moy = n.moyenne_devoirs !== null ? n.moyenne_devoirs.toFixed(2) : '—';
      const moyColor = n.moyenne_devoirs !== null
        ? (n.moyenne_devoirs >= 10 ? '#16a34a' : '#dc2626')
        : '#888';
      return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f8f9fa'}">
        <td style="padding:4px 6px;text-align:center;border:1px solid #ddd">${i + 1}</td>
        <td style="padding:4px 6px;border:1px solid #ddd;font-family:monospace">${n.matricule}</td>
        <td style="padding:4px 6px;border:1px solid #ddd"><b>${n.nom}</b> ${n.prenoms}</td>
        <td style="padding:4px 6px;text-align:center;border:1px solid #ddd">${n.classe || ''}</td>
        <td style="padding:4px 6px;text-align:center;border:1px solid #ddd">${n.absent1 ? '<span style="color:#d97706">ABS</span>' : (n.note1 !== null ? n.note1.toFixed(2) : '—')}</td>
        <td style="padding:4px 6px;text-align:center;border:1px solid #ddd">${n.absent2 ? '<span style="color:#d97706">ABS</span>' : (n.note2 !== null ? n.note2.toFixed(2) : '—')}</td>
        <td style="padding:4px 6px;text-align:center;border:1px solid #ddd;font-weight:bold;color:${moyColor}">${moy}</td>
      </tr>`;
    }).join('');

    const stats = mat.stats;

    return `
      ${idx > 0 ? '<div style="page-break-before:always"></div>' : ''}
      <div style="margin-bottom:20px">
        <div style="background:#1e3a5f;color:white;padding:8px 12px;border-radius:4px 4px 0 0;display:flex;justify-content:space-between;align-items:center">
          <div>
            <span style="font-weight:bold;font-size:13px">${mat.ue_code} — ${mat.ue_nom}</span><br/>
            <span style="font-size:12px">${mat.matiere_code} — ${mat.matiere_nom} (${mat.credits} crédits)</span>
          </div>
          <div style="text-align:right;font-size:11px">
            <span>Effectif: ${stats.total}</span><br/>
            <span>Moy: ${stats.moyenne_classe.toFixed(2)}/20 | Réussite: ${stats.taux_reussite.toFixed(1)}%</span>
          </div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:11px">
          <thead>
            <tr style="background:#e2e8f0;font-weight:bold;font-size:10px">
              <th style="padding:5px;border:1px solid #ddd;width:30px">N°</th>
              <th style="padding:5px;border:1px solid #ddd;width:80px">Matricule</th>
              <th style="padding:5px;border:1px solid #ddd">NOM et Prénom(s)</th>
              <th style="padding:5px;border:1px solid #ddd;width:60px">Classe</th>
              <th style="padding:5px;border:1px solid #ddd;width:55px">D1</th>
              <th style="padding:5px;border:1px solid #ddd;width:55px">D2</th>
              <th style="padding:5px;border:1px solid #ddd;width:60px">Moyenne</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:#e2e8f0;font-weight:bold;font-size:10px">
              <td colspan="4" style="padding:5px;border:1px solid #ddd;text-align:right">
                Statistiques — Abs D1: ${stats.absents_d1} | Abs D2: ${stats.absents_d2}
              </td>
              <td colspan="2" style="padding:5px;border:1px solid #ddd;text-align:center">
                Moy: ${stats.moyenne_classe.toFixed(2)}/20
              </td>
              <td style="padding:5px;border:1px solid #ddd;text-align:center">
                ${stats.taux_reussite.toFixed(1)}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>`;
  }).join('');

  const totalEtudiants = data.matieres.reduce((s, m) => s + m.stats.total, 0);
  const totalMatieres = data.matieres.length;

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>Rapport Notes Devoirs — ${semestre}</title>
<style>
  @page { size: A4 portrait; margin: 12mm 10mm; }
  @media print { .no-print { display: none !important; } }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 15px; }
</style>
</head><body>
  <div style="text-align:center;margin-bottom:15px">
    <div style="font-size:8px;color:#666;text-transform:uppercase;letter-spacing:1px">République du Congo</div>
    <div style="font-size:16px;font-weight:bold;color:#1e3a5f;margin:4px 0">INSTITUT INTERNATIONAL 2i</div>
    <div style="font-size:9px;color:#888;font-style:italic">Agrément N° 554/MESRSIT/DGES/2012</div>
    <div style="font-size:11px;margin-top:6px;color:#2563eb;font-weight:bold">Année Académique ${anneeAcademique}</div>
  </div>

  <div style="background:#1e3a5f;color:white;text-align:center;padding:10px;border-radius:4px;margin-bottom:5px">
    <div style="font-size:15px;font-weight:bold;letter-spacing:1px">RAPPORT DES NOTES DE DEVOIRS — Semestre ${semestre.replace('S', '')}</div>
  </div>

  ${filtresText ? `<div style="text-align:center;font-size:11px;color:#555;margin-bottom:12px;font-style:italic">${filtresText}</div>` : ''}

  <div style="display:flex;justify-content:space-between;font-size:10px;color:#666;margin-bottom:10px;border-bottom:1px solid #ddd;padding-bottom:5px">
    <span>${totalMatieres} matière(s)</span>
    <span>Imprimé le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
  </div>

  ${matieresSections}

  <div style="margin-top:30px;display:flex;justify-content:space-between">
    <div style="text-align:center;width:45%">
      <div style="font-size:11px;font-weight:bold">Le Responsable Pédagogique</div>
      <div style="margin-top:40px;border-top:1px solid #333;padding-top:5px;font-size:10px">Signature et cachet</div>
    </div>
    <div style="text-align:center;width:45%">
      <div style="font-size:11px;font-weight:bold">Le Directeur</div>
      <div style="margin-top:40px;border-top:1px solid #333;padding-top:5px;font-size:10px">Signature et cachet</div>
    </div>
  </div>
</body></html>`;
}

export const RapportNotesPreview: React.FC<RapportNotesPreviewProps> = ({ onClose }) => {
  const [semestre, setSemestre] = useState('S1');
  const [pole, setPole] = useState('');
  const [niveau, setNiveau] = useState('');
  const [parcours, setParcours] = useState('');
  const [option, setOption] = useState('');
  const [classe, setClasse] = useState('');

  const [fetchRapport, { data, isLoading, error }] = useLazyGetRapportImpressionQuery();
  const anneeAcademique = getAnneeAcademique() || '2025-2026';

  const handleFetch = () => {
    const params: any = { semestre };
    if (pole) params.pole = pole;
    if (niveau) params.niveau = niveau;
    if (parcours) params.parcours = parcours;
    if (option) params.option = option;
    if (classe) params.classe = classe;
    fetchRapport(params);
  };

  const handlePrint = () => {
    if (!data) return;
    const html = buildRapportHTML(data, anneeAcademique);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 600);
    }
  };

  const selectClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Printer size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Impression Notes par Groupe</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Filtres */}
        <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-gray-400" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filtres</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre *</label>
              <select value={semestre} onChange={(e) => setSemestre(e.target.value)} className={selectClass}>
                <option value="S1">S1</option>
                <option value="S2">S2</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle</label>
              <select value={pole} onChange={(e) => setPole(e.target.value)} className={selectClass}>
                <option value="">Tous</option>
                {POLES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)} className={selectClass}>
                <option value="">Tous</option>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Parcours</label>
              <input
                type="text"
                value={parcours}
                onChange={(e) => setParcours(e.target.value)}
                placeholder="Ex: Génie Info"
                className={selectClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Option</label>
              <input
                type="text"
                value={option}
                onChange={(e) => setOption(e.target.value)}
                placeholder="Ex: Informatique"
                className={selectClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Classe</label>
              <input
                type="text"
                value={classe}
                onChange={(e) => setClasse(e.target.value)}
                placeholder="Ex: L1INFO"
                className={selectClass}
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleFetch}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
              Charger l'aperçu
            </button>
            {data && data.matieres.length > 0 && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <Printer size={16} />
                Imprimer ({data.matieres.length} matière{data.matieres.length > 1 ? 's' : ''})
              </button>
            )}
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="text-center py-8 text-red-500">
              Erreur lors du chargement. Vérifiez les filtres.
            </div>
          )}

          {data && data.matieres.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <FileText size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">Aucune note trouvée</p>
              <p className="text-sm mt-1">Essayez de modifier les filtres</p>
            </div>
          )}

          {data && data.matieres.length > 0 && (
            <div className="space-y-6">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {data.matieres.length} matière(s) — {data.matieres.reduce((s, m) => s + m.stats.total, 0)} notes au total
              </div>

              {data.matieres.map((mat, idx) => (
                <div key={idx} className="border dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-blue-900 text-white px-4 py-2 flex justify-between items-center">
                    <div>
                      <span className="font-bold text-sm">{mat.ue_code} — {mat.ue_nom}</span>
                      <span className="text-blue-200 text-xs ml-3">{mat.matiere_code} — {mat.matiere_nom} ({mat.credits} cr.)</span>
                    </div>
                    <div className="text-xs text-blue-200">
                      {mat.stats.total} ét. | Moy: {mat.stats.moyenne_classe.toFixed(2)} | {mat.stats.taux_reussite.toFixed(1)}%
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <th className="px-2 py-1.5 text-left w-8">N°</th>
                          <th className="px-2 py-1.5 text-left w-20">Matricule</th>
                          <th className="px-2 py-1.5 text-left">Nom & Prénoms</th>
                          <th className="px-2 py-1.5 text-center w-14">Classe</th>
                          <th className="px-2 py-1.5 text-center w-12">D1</th>
                          <th className="px-2 py-1.5 text-center w-12">D2</th>
                          <th className="px-2 py-1.5 text-center w-14">Moy.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mat.notes.slice(0, 10).map((n, i) => (
                          <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-800/50'}>
                            <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                            <td className="px-2 py-1 font-mono text-xs">{n.matricule}</td>
                            <td className="px-2 py-1"><b>{n.nom}</b> {n.prenoms}</td>
                            <td className="px-2 py-1 text-center text-gray-500">{n.classe}</td>
                            <td className="px-2 py-1 text-center">
                              {n.absent1 ? <span className="text-amber-500">ABS</span> : (n.note1 !== null ? n.note1.toFixed(2) : '—')}
                            </td>
                            <td className="px-2 py-1 text-center">
                              {n.absent2 ? <span className="text-amber-500">ABS</span> : (n.note2 !== null ? n.note2.toFixed(2) : '—')}
                            </td>
                            <td className={`px-2 py-1 text-center font-bold ${n.moyenne_devoirs !== null ? (n.moyenne_devoirs >= 10 ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}`}>
                              {n.moyenne_devoirs !== null ? n.moyenne_devoirs.toFixed(2) : '—'}
                            </td>
                          </tr>
                        ))}
                        {mat.notes.length > 10 && (
                          <tr>
                            <td colSpan={7} className="px-2 py-1.5 text-center text-gray-400 italic text-xs">
                              ... et {mat.notes.length - 10} autres étudiants (visible à l'impression)
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!data && !isLoading && !error && (
            <div className="text-center py-12 text-gray-400">
              <Printer size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sélectionnez vos filtres et cliquez sur "Charger l'aperçu"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
